'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { BellOff, X } from 'lucide-react';
import { GlassSelectContent, GradientDivider } from '@/shared/ui/glass';
import { Select, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { KIND_GROUPS, KIND_GROUP_LABEL, URGENCIES, kindByKey, kindsInGroup, type Urgency } from '../../model/notification-kinds';
import { useNotificationStore } from '../../model/notification-store';
import { URGENCY_LABEL } from '../../lib/grouping';
import { atOf, hourOf } from '../../model/preference-diff';

/**
 * ONE GLASS LAYER PER STACKING CONTEXT, and the cover is it.
 *
 * `centre-surface.tsx` is `bg-white/70 backdrop-blur-xl`, and below 1000 this
 * panel renders inside `GlassPopoverContent`, which is a second. Every section
 * in here carrying its own `backdrop-blur-xl` made three stacked blurs for one
 * visual depth, which renders poorly and costs on every scroll. The design
 * system's rule is the one applied: the container is glass and the things
 * inside it are solid.
 */
const SECTION = 'rounded-2xl bg-white p-3 shadow-[0_1px_3px_rgba(15,23,42,0.05)] dark:bg-white/12';

/** The twenty-four hours, as values. */
const HOURS: readonly number[] = Array.from({
  length: 24
}, (_, hour) => hour);

/**
 * The hour in the reader's own convention, so 13 reads as "1 pm" where that is
 * what a clock says and as "13:00" where it is not. `toLocaleTimeString` is
 * what knows which, and it is the same call every other time on this surface
 * goes through.
 */
const hourLabel = (hour: number): string => new Date(2000, 0, 1, hour).toLocaleTimeString(undefined, {
  hour: 'numeric'
});

/**
 * What the broker composes, which is everything except the list of kinds.
 *
 * The digest is the centrepiece here rather than a checkbox at the bottom,
 * because here the digest is what a day becomes. Change the hour
 * and yesterday closes at a different moment; change what it covers and every
 * digest sentence on the page rewrites; switch it off and the days keep
 * collapsing but stop saying anything, which is the honest picture of what the
 * preference is worth.
 *
 * Mutes are three scopes and a list, never a boolean per kind in a column. The
 * one thing a mute cannot do is hide a `now`: the store exempts that class, so
 * a muted client with an overdue renewal still reaches the carried strip. The
 * person who muted a client did not mute a fire.
 */
export function PreferencesPanel(t0) {
  const $ = _c(68);
  const {
    className
  } = t0;
  const preferences = useNotificationStore(_temp);
  const clients = useNotificationStore(_temp2);
  const boards = useNotificationStore(_temp3);
  const setDigest = useNotificationStore(_temp4);
  const digestConsent = useNotificationStore(_temp5);
  const mute = useNotificationStore(_temp6);
  const unmute = useNotificationStore(_temp7);
  const setKindEnabled = useNotificationStore(_temp8);
  const [openGroup, setOpenGroup] = useState(null);
  const digest = preferences.digest;
  const digestUnset = digestConsent === null || !digestConsent.enabled || digestConsent.timezone === null;
  let t1;
  if ($[0] !== boards || $[1] !== clients) {
    t1 = (scope, target) => {
      if (scope === "client") {
        return clients.find(c => c.id === target)?.name ?? target;
      }
      if (scope === "board") {
        return boards.find(b => b.id === target)?.name ?? target;
      }
      return kindByKey(target)?.label ?? target;
    };
    $[0] = boards;
    $[1] = clients;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const targetLabel = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("space-y-3 text-[12px] text-[#44546F] dark:text-white/70", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#091E42] dark:text-white">When a day closes</span>;
    t4 = <p className="mt-1.5 leading-relaxed">A day closes at this hour the morning after, and what it leaves behind is the sentence at the head of that day. Today never closes.</p>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <label htmlFor="notification-digest-hour" className="text-[11px]">Closes at</label>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== digest.at) {
    t6 = hourOf(digest.at);
    $[8] = digest.at;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = String(t6);
  let t8;
  if ($[10] !== setDigest) {
    t8 = value => setDigest({
      at: atOf(Number(value))
    });
    $[10] = setDigest;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SelectTrigger id="notification-digest-hour" size="sm" className="min-w-20 gap-1.5 rounded-lg border-0 bg-white px-2.5 py-0 text-[12px] font-medium text-[#091E42] shadow-[0_1px_3px_rgba(15,23,42,0.08)] transition-shadow data-[size=sm]:h-7 hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)] focus-visible:ring-0 focus-visible:shadow-[0_2px_8px_rgba(86,81,255,0.22)] data-[state=open]:shadow-[0_2px_8px_rgba(86,81,255,0.22)] dark:bg-white/12 dark:text-white dark:hover:bg-white/16 [&_svg]:size-3.5"><SelectValue /></SelectTrigger>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <GlassSelectContent className="max-h-[min(16rem,var(--radix-select-content-available-height))]">{HOURS.map(_temp9)}</GlassSelectContent>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t7 || $[15] !== t8) {
    t11 = <div className="mt-2 flex items-center gap-2">{t5}<Select value={t7} onValueChange={t8}>{t9}{t10}</Select></div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== digestUnset) {
    t12 = digestUnset ? <p className="mt-2 text-[11px] leading-relaxed text-[#44546F]/85 dark:text-white/60">Turn the daily digest on in your notification settings to choose when a day closes.</p> : null;
    $[17] = digestUnset;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="mt-2.5 text-[11px] font-medium text-[#091E42] dark:text-white">What the sentence names</p>;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== digest.covers || $[21] !== setDigest) {
    t14 = URGENCIES.map(u => <CoverChip key={u} urgency={u} on={digest.covers.includes(u)} onToggle={() => setDigest({
      covers: digest.covers.includes(u) ? digest.covers.filter(c_0 => c_0 !== u) : [...digest.covers, u]
    })} />);
    $[20] = digest.covers;
    $[21] = setDigest;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <div className="mt-1 flex flex-wrap gap-1">{t14}</div>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t15) {
    t16 = <section className={SECTION}>{t3}{t4}{t11}{t12}{t13}{t15}</section>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#091E42] dark:text-white">Muted</span>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== preferences.mutes || $[31] !== targetLabel || $[32] !== unmute) {
    t18 = preferences.mutes.length === 0 ? <p className="mt-1.5 leading-relaxed">Nothing is muted. Mute a client, a board or a kind from any row, and lift it here. A mute is indefinite and is never a delete, so it can always come back. Anything that needs you now still comes through.</p> : <ul className="mt-1.5 space-y-1">{preferences.mutes.map(m => <li key={`${m.scope}:${m.target}`} className="flex items-center gap-1.5"><BellOff className="h-3 w-3 shrink-0 text-[#44546F]" /><span className="truncate text-[12px]">{targetLabel(m.scope, m.target)}</span><span className="shrink-0 text-[10px] uppercase tracking-wide text-[#44546F]/70 dark:text-white/50">{m.scope}</span><button type="button" onClick={() => unmute(m.scope, m.target)} aria-label={`Unmute ${targetLabel(m.scope, m.target)}`} className="-my-2.5 ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#44546F] transition-colors hover:text-[#091E42] dark:text-white/70 dark:hover:text-white"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08)] dark:bg-white/16"><X className="h-3 w-3" /></span></button></li>)}</ul>;
    $[30] = preferences.mutes;
    $[31] = targetLabel;
    $[32] = unmute;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p className="mt-2.5 text-[11px] font-medium text-[#091E42] dark:text-white">Quick mute</p>;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== boards || $[36] !== mute) {
    let t21;
    if ($[38] !== mute) {
      t21 = b_0 => <button key={b_0.id} type="button" onClick={() => mute({
        scope: "board",
        target: b_0.id,
        until: null
      })} aria-label={`Mute the ${b_0.name} board`} className="rounded-full bg-white px-2.5 py-1.5 text-[11px] text-[#44546F] shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:text-[#091E42] hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)] dark:bg-white/12 dark:text-white/70 dark:hover:text-white">{b_0.name} board</button>;
      $[38] = mute;
      $[39] = t21;
    } else {
      t21 = $[39];
    }
    t20 = boards.map(t21);
    $[35] = boards;
    $[36] = mute;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[40] !== clients || $[41] !== mute) {
    let t22;
    if ($[43] !== mute) {
      t22 = c_1 => <button key={c_1.id} type="button" onClick={() => mute({
        scope: "client",
        target: c_1.id,
        until: null
      })} aria-label={`Mute ${c_1.name}`} className="rounded-full bg-white px-2.5 py-1.5 text-[11px] text-[#44546F] shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:text-[#091E42] hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)] dark:bg-white/12 dark:text-white/70 dark:hover:text-white">{c_1.name.split(" ")[0]}</button>;
      $[43] = mute;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    t21 = clients.slice(0, 3).map(t22);
    $[40] = clients;
    $[41] = mute;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[45] !== t20 || $[46] !== t21) {
    t22 = <div className="mt-1 flex flex-wrap gap-1">{t20}{t21}</div>;
    $[45] = t20;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t18 || $[49] !== t22) {
    t23 = <section className={SECTION}>{t17}{t18}{t19}{t22}</section>;
    $[48] = t18;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#091E42] dark:text-white">Kinds</span>;
    t25 = <p className="mt-1.5 leading-relaxed">The platform's whole catalogue, and the only closed list here. A kind exists because the server registers it, so a firm cannot invent one, but anybody can switch any of them off for themselves.</p>;
    $[51] = t24;
    $[52] = t25;
  } else {
    t24 = $[51];
    t25 = $[52];
  }
  let t26;
  if ($[53] !== openGroup || $[54] !== preferences.disabledKinds || $[55] !== setKindEnabled) {
    t26 = KIND_GROUPS.map(group => {
      const kinds = kindsInGroup(group);
      const off = kinds.filter(k => preferences.disabledKinds.includes(k.key)).length;
      const open = openGroup === group;
      return <li key={group}><button type="button" onClick={() => setOpenGroup(open ? null : group)} aria-expanded={open} className="flex min-h-11 w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-white/70 dark:hover:bg-white/10"><span className="text-[12px] text-[#091E42] dark:text-white">{KIND_GROUP_LABEL[group]}</span><span className="ml-auto text-[11px] tabular-nums text-[#44546F] dark:text-white/60">{off === 0 ? `${kinds.length} on` : `${kinds.length - off} of ${kinds.length} on`}</span></button>{open ? <ul className="mb-1 ml-1.5 space-y-0.5 border-l border-black/5 pl-2 dark:border-white/10">{kinds.map(k_0 => {
            const on = !preferences.disabledKinds.includes(k_0.key);
            return <li key={k_0.key} className="flex min-h-11 items-center gap-2"><span className="truncate text-[11px]">{k_0.label}</span><button type="button" aria-pressed={on} aria-label={`${k_0.label} notifications`} onClick={() => setKindEnabled(k_0.key, !on)} className="ml-auto flex h-11 shrink-0 items-center pl-3"><span className={cn("rounded-full px-2.5 py-1 text-[10px] font-medium transition-shadow", on ? "bg-white text-[#091E42] shadow-[0_2px_8px_rgba(86,81,255,0.15)] dark:bg-white/16 dark:text-white" : "bg-white/60 text-[#44546F] dark:bg-white/6 dark:text-white/60")}>{on ? "On" : "Off"}</span></button></li>;
          })}</ul> : null}</li>;
    });
    $[53] = openGroup;
    $[54] = preferences.disabledKinds;
    $[55] = setKindEnabled;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t26) {
    t27 = <ul className="mt-2 space-y-0.5">{t26}</ul>;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  let t29;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <GradientDivider className="my-2" />;
    t29 = <p className="text-[11px] leading-relaxed text-[#44546F]/85 dark:text-white/60">Switching a kind off here stops it being written at all, not just hidden: the server reads this list before it raises a notification. The one thing it cannot do is hide something that needs you now, because the person who switched a kind off did not switch off a fire.</p>;
    $[59] = t28;
    $[60] = t29;
  } else {
    t28 = $[59];
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t27) {
    t30 = <section className={SECTION}>{t24}{t25}{t27}{t28}{t29}</section>;
    $[61] = t27;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t16 || $[64] !== t2 || $[65] !== t23 || $[66] !== t30) {
    t31 = <div className={t2}>{t16}{t23}{t30}</div>;
    $[63] = t16;
    $[64] = t2;
    $[65] = t23;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  return t31;
}
function _temp9(hour) {
  return <SelectItem key={hour} value={String(hour)} className="text-[12px] whitespace-nowrap">{hourLabel(hour)}</SelectItem>;
}
function _temp8(s_6) {
  return s_6.setKindEnabled;
}
function _temp7(s_5) {
  return s_5.unmute;
}
function _temp6(s_4) {
  return s_4.mute;
}
function _temp5(s_3) {
  return s_3.digestConsent;
}
function _temp4(s_2) {
  return s_2.setDigest;
}
function _temp3(s_1) {
  return s_1.boards;
}
function _temp2(s_0) {
  return s_0.clients;
}
function _temp(s) {
  return s.preferences;
}
function CoverChip(t0) {
  const $ = _c(10);
  const {
    urgency,
    on,
    onToggle
  } = t0;
  const t1 = URGENCY_LABEL[urgency];
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toLowerCase();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `Name ${t2} in the day's sentence`;
  const t4 = on ? "bg-white text-[#091E42] shadow-[0_2px_8px_rgba(86,81,255,0.15)] dark:bg-white/16 dark:text-white" : "bg-white/60 text-[#44546F] hover:text-[#091E42] dark:bg-white/6 dark:text-white/60 dark:hover:text-white";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("whitespace-nowrap rounded-full px-2.5 py-1.5 text-[11px] font-medium transition-shadow", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = URGENCY_LABEL[urgency];
  let t7;
  if ($[4] !== on || $[5] !== onToggle || $[6] !== t3 || $[7] !== t5 || $[8] !== t6) {
    t7 = <button type="button" aria-pressed={on} aria-label={t3} onClick={onToggle} className={t5}>{t6}</button>;
    $[4] = on;
    $[5] = onToggle;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}